import styled from "@emotion/styled";
import { trackEvent } from "contexts/mixpanel";
import { useLocation } from "react-router-dom";
import { AllCaps, Callout, FlexRow, H2, Link, Stack } from "atoms";
import { useAuth } from "features/auth";
import { catalog, ExtraOCSubmissionsLink } from "features/commerce";
import { formatDate } from "utils/date";
import { useUsersOpenCallVotes } from "../api/useUsersOpenCallVotes";
import { getOpenCallTag } from "../utils/getOpenCallTag";
import type { OpenCall } from "../types";
import { useOpenCallUserSubmissions } from "../api/useOpenCallUserSubmissions";

type OpenCallButtonProps = {
  openCall: OpenCall;
};

const trackSignUp = (action: string) => () =>
  trackEvent({
    name: "Button Click",
    ButtonName: "Sign Up",
    ButtonContext:
      action === "submit" ? "Open Call Submission" : "Open Call Vote",
  });

const trackSignIn = (action: string) => () =>
  trackEvent({
    name: "Button Click",
    ButtonName: "Sign In",
    ButtonContext:
      action === "submit" ? "Open Call Submission" : "Open Call Vote",
  });

const trackSubmit = () =>
  trackEvent({
    name: "Button Click",
    ButtonName: "Open Call Submit",
    ButtonContext: "Open Call Submission",
  });

// const trackSubmitEdit = () =>
//   trackEvent({
//     name: "Button Click",
//     ButtonName: "Open Call Submit Edit",
//     ButtonContext: "Open Call Submission",
//   });

const trackVote = () =>
  trackEvent({
    name: "Button Click",
    ButtonName: "Open Call Vote",
    ButtonContext: "Open Call Submission",
  });

function OpenCallButton({ openCall }: OpenCallButtonProps) {
  const { user } = useAuth();
  const { data } = useOpenCallUserSubmissions(openCall.id, user?.id);
  const { data: usersVotes } = useUsersOpenCallVotes();

  if (!data) return null;

  const {
    data: submissions,
    // isEligible,
    canPurchaseSubmissions,
    userSubmissionAllowance,
  } = data;

  // Find max possible for this open call according to it's settings
  const freeSubmissions = 1;
  const paidSubmissions = openCall.numberOfPaidSubmissions ?? 0;
  const maxSubmissions = freeSubmissions + paidSubmissions;
  // Determine how many the user has already submitted
  const numberSubmitted = submissions.length ?? 0;
  // Determine how many the user has remaining assuming they purchase all extras
  const maxRemaining = maxSubmissions - numberSubmitted;
  // Determine if the user can currently submit, i.e. check if they haven't yet met their allowance
  const canSubmit = numberSubmitted < userSubmissionAllowance;

  const tag = getOpenCallTag(openCall);

  if (tag === "Submissions Open") {
    if (!user) {
      return (
        <SignInToAct
          action="submit"
          voteDate={openCall.communityVotingStartDate}
        />
      );
    }

    let submitText = "Additional Artworks";
    if (numberSubmitted === 0) {
      submitText = canPurchaseSubmissions ? "1 For Free" : "Now";
    }
    const submitBtnVariant =
      numberSubmitted === 0 && canPurchaseSubmissions ? "secondary" : "primary";

    return (
      <>
        {canSubmit && (
          <Link
            arrow="r"
            to="submit"
            onClick={trackSubmit}
            variant={submitBtnVariant}
          >
            Submit {submitText}
          </Link>
        )}

        {canPurchaseSubmissions && (
          <ExtraOCSubmissionsLink
            openCallId={openCall.id}
            title={openCall.title}
            variant="primary"
            arrow="r"
          >
            Submit {numberSubmitted ? `${maxRemaining} More` : maxSubmissions}{" "}
            For {catalog.extraOCSubmissions.cost}
          </ExtraOCSubmissionsLink>
        )}
      </>
    );
  }

  if (tag === "Voting Soon" && !user)
    return (
      <SignInToAct
        action="vote soon"
        voteDate={openCall.communityVotingStartDate}
      />
    );

  if (
    tag === "Voting Open" &&
    user &&
    !usersVotes?.openCallIds.includes(openCall.id)
  )
    return (
      <Link to="vote" variant="primary" onClick={trackVote}>
        Vote now
      </Link>
    );

  if (tag === "Voting Open" && !user)
    return (
      <SignInToAct action="vote" voteDate={openCall.communityVotingStartDate} />
    );

  return null;
}

function SignInToAct({
  action,
  voteDate,
}: {
  action: "submit" | "vote" | "vote soon";
  voteDate: string | undefined;
}) {
  const location = useLocation();
  let fromPathname = "";
  if (action === "submit") {
    fromPathname = "submit";
  }
  if (action === "vote") {
    fromPathname = "vote";
  }
  if (action === "vote soon" && !voteDate) return null;

  // Create Link `from` state to redirect to submission form after sign-in, not back here
  const submitLocation = {
    ...location,
    pathname: `${location.pathname}/${fromPathname}`,
  };

  return (
    <SCallout>
      <FlexRow gap="10px">
        <Stack>
          <H2 size="sm">
            {action === "submit" && <>Sign in to submit your&nbsp;artwork</>}
            {action === "vote soon" && (
              <>
                Join <AllCaps>Hug</AllCaps> to vote on{" "}
                {formatDate(voteDate, { month: "long", day: "numeric" })}
              </>
            )}
            {action === "vote" && <>Sign in to vote</>}
          </H2>
        </Stack>

        <FlexRow gap="10px">
          <Link
            to="/register"
            variant="primary"
            size="xs"
            state={{ from: submitLocation }}
            onClick={trackSignUp(action)}
          >
            Create Account
          </Link>
          {action !== "vote soon" && (
            <Link
              to="/sign-in"
              variant="primary"
              size="xs"
              state={{ from: submitLocation }}
              onClick={trackSignIn(action)}
            >
              Sign in
            </Link>
          )}
        </FlexRow>
      </FlexRow>
    </SCallout>
  );
}

const SCallout = styled(Callout)(({ theme }) => ({
  color: theme.colors.fg,
  flex: "0 0 100%",
  maxWidth: "max-content",
  "&&": { padding: ".66rem" },
}));

export { OpenCallButton };
